import { useEffect, useMemo } from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { CalendarHeart, Clock3 } from 'lucide-react'
import { PinDayLogo } from '@/components/ui/PinDayLogo'
import {
  getPreferredMaintenanceLocale,
  normalizeMaintenanceLocale,
  SUPPORTED_MAINTENANCE_LOCALES,
  type MaintenanceLocale,
} from '@/lib/maintenance-mode'

const MAINTENANCE_COPY: Record<
  MaintenanceLocale,
  {
    htmlLang: string
    status: string
    title: string
    body: string
    note: string
    returning: string
    pauseLabel: string
  }
> = {
  en: {
    htmlLang: 'en',
    status: 'A tiny pause',
    title: 'PinDay is taking a quiet little break.',
    body: 'We are refreshing a few things behind the scenes, so logins and new accounts are temporarily closed.',
    note: 'Thanks for the patience. We will open the calendar again when everything is ready.',
    returning: 'Back soon',
    pauseLabel: 'Maintenance mode',
  },
  pt: {
    htmlLang: 'pt-BR',
    status: 'Uma pausa rapidinha',
    title: 'O PinDay está fazendo uma pausa tranquila.',
    body: 'Estamos ajustando algumas coisas nos bastidores, então logins e novas contas estão temporariamente fechados.',
    note: 'Agradecemos pela paciência. A agenda volta a abrir quando tudo estiver pronto.',
    returning: 'Voltamos em breve',
    pauseLabel: 'Modo manutenção',
  },
  es: {
    htmlLang: 'es',
    status: 'Una pausa breve',
    title: 'PinDay se tomó una pausa tranquila.',
    body: 'Estamos ajustando algunas cosas detrás de escena, así que los inicios de sesión y las cuentas nuevas están cerrados temporalmente.',
    note: 'Gracias por la paciencia. Volveremos a abrir el calendario cuando todo esté listo.',
    returning: 'Volvemos pronto',
    pauseLabel: 'Modo mantenimiento',
  },
}

const LANGUAGE_LABELS: Record<MaintenanceLocale, string> = {
  en: 'English',
  pt: 'Português',
  es: 'Español',
}

function useMaintenanceLocale(): MaintenanceLocale {
  const { locale } = useParams<{ locale?: string }>()
  const location = useLocation()

  return useMemo(() => {
    const queryLocale = normalizeMaintenanceLocale(new URLSearchParams(location.search).get('lang'))
    const pathLocale = normalizeMaintenanceLocale(locale)

    return pathLocale || queryLocale || getPreferredMaintenanceLocale()
  }, [locale, location.search])
}

export function MaintenancePage() {
  const locale = useMaintenanceLocale()
  const copy = MAINTENANCE_COPY[locale]

  useEffect(() => {
    document.title = `${copy.returning} | PinDay`
    document.documentElement.lang = copy.htmlLang
    document.documentElement.dir = 'ltr'
  }, [copy])

  return (
    <main className="relative isolate flex min-h-screen overflow-hidden bg-[#f8fbff] text-[var(--pd-foreground)]">
      <div className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_18%_18%,rgba(240,138,36,0.17),transparent_30%),radial-gradient(circle_at_84%_12%,rgba(55,125,116,0.16),transparent_28%),linear-gradient(135deg,#f8fbff_0%,#eef7f4_48%,#ffffff_100%)]" />
      <div className="absolute inset-x-0 bottom-0 -z-10 h-1/2 bg-[linear-gradient(180deg,transparent_0%,rgba(11,42,74,0.08)_100%)]" />

      <section className="mx-auto grid w-full max-w-6xl items-center gap-10 px-5 py-8 sm:px-8 lg:grid-cols-[0.95fr_1.05fr] lg:px-10">
        <motion.div
          className="order-2 lg:order-1"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
        >
          <PinDayLogo size="lg" />

          {/* <div className="mt-10 inline-flex items-center gap-2 rounded-full border border-[#0b2a4a]/10 bg-white/65 px-3 py-2 text-sm font-semibold text-[#123a63] shadow-[0_12px_40px_rgba(11,42,74,0.08)] backdrop-blur">
            <Clock3 className="h-4 w-4 text-[#f08a24]" />
            {copy.status}
          </div> */}

          <h1 className="mt-6 max-w-3xl text-4xl font-semibold leading-[1.04] tracking-normal text-[#0b2a4a] sm:text-5xl lg:text-6xl">
            {copy.title}
          </h1>

          <p className="mt-6 max-w-xl text-base leading-7 text-[#0b2a4a]/72 sm:text-lg">
            {copy.body}
          </p>

          <p className="mt-4 max-w-xl text-sm leading-6 text-[#0b2a4a]/62 sm:text-base">
            {copy.note}
          </p>

          <nav className="flex flex-wrap gap-2 mt-8" aria-label="Maintenance language">
            {SUPPORTED_MAINTENANCE_LOCALES.map((language) => (
              <Link
                key={language}
                to={`/${language}/maintenance`}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition duration-200 ${
                  language === locale
                    ? 'border-[#0b2a4a] bg-[#0b2a4a] text-white shadow-[0_14px_30px_rgba(11,42,74,0.18)]'
                    : 'border-[#0b2a4a]/12 bg-white/60 text-[#0b2a4a]/72 hover:border-[#f08a24]/70 hover:text-[#0b2a4a]'
                }`}
              >
                {LANGUAGE_LABELS[language]}
              </Link>
            ))}
          </nav>
        </motion.div>

        <motion.div
          className="flex justify-center order-1 lg:order-2"
          initial={{ opacity: 0, scale: 0.96, y: 18 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: 'easeOut' }}
        >
          <div className="relative w-full max-w-[520px] overflow-hidden rounded-[var(--pd-radius)] border border-white/70 bg-white/72 p-5 shadow-[0_28px_90px_rgba(11,42,74,0.18)] backdrop-blur-xl sm:p-7">
            <div className="absolute right-6 top-6 h-3 w-3 rounded-full bg-[#f08a24]" />
            <div className="absolute right-12 top-6 h-3 w-3 rounded-full bg-[#0b2a4a]/20" />
            <div className="absolute right-[4.5rem] top-6 h-3 w-3 rounded-full bg-[#0b2a4a]/12" />

            <div className="mt-8 rounded-[var(--pd-radius-sm)] bg-[#0b2a4a] p-5 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12)]">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-white/60">{copy.pauseLabel}</p>
                  <p className="mt-1 text-2xl font-semibold">{copy.returning}</p>
                </div>
                <div className="flex h-14 w-14 items-center justify-center rounded-[var(--pd-radius-sm)] bg-[#f08a24] text-white">
                  <CalendarHeart className="h-7 w-7" />
                </div>
              </div>

              <div className="grid grid-cols-7 gap-2 mt-8" aria-hidden="true">
                {Array.from({ length: 35 }).map((_, index) => {
                  const isHighlighted = [9, 10, 11, 17, 24].includes(index)
                  return (
                    <motion.div
                      key={index}
                      className={`aspect-square rounded-lg ${
                        isHighlighted ? 'bg-[#f08a24]' : 'bg-white/10'
                      }`}
                      animate={isHighlighted ? { opacity: [0.68, 1, 0.68] } : undefined}
                      transition={{ duration: 3, repeat: Infinity, delay: index * 0.04 }}
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </main>
  )
}
